import Alpine from "alpinejs";
import { dispatch } from "../utils/events.ts";

interface StoredMethods {
  [key: string]: Function; //  Allows any property name (string) mapped to a Function.
}

function manageHistoryMethods(scriptSrc: string): Promise<void> {
  return new Promise((resolve, reject) => {
    // a) Save the original methods
    const originalMethods: StoredMethods = {};

    //Crucially check if they exist, and only backup up existing methods.
    if (typeof history.replaceState === "function") {
      originalMethods.replaceState = history.replaceState;
    }
    if (typeof history.pushState === "function") {
      originalMethods.pushState = history.pushState;
    }

    // Create a closure to encapsulate the restoration
    const restoreMethods = () => {
      // c) Set back the original methods.  Iterate and restore ONLY if they were backed up
      for (const methodName in originalMethods) {
        if (originalMethods.hasOwnProperty(methodName)) {
          //Type assertion as we know history[methodName] is a function.
          (history as any)[methodName] = originalMethods[methodName];
        }
      }
    };

    // b) Load the script (dynamically)
    const script = document.createElement("script");
    script.src = scriptSrc;
    script.async = true; // Important: Load asynchronously

    script.onload = () => {
      restoreMethods();
      resolve();
    };

    script.onerror = () => {
      restoreMethods();
      reject();
    };

    document.head.appendChild(script);
  });
}

export function loadOfficeJsApi(): Promise<void> {
  return manageHistoryMethods(
    "https://appsforoffice.microsoft.com/lib/1/hosted/office.js"
  );
}

interface IState {
  enabled: boolean | undefined;
  loaded: boolean;
}

const _state: IState = { enabled: undefined, loaded: false };
const state = Alpine.reactive(_state);

async function init() {
  try {
    state.enabled = undefined;
    state.loaded = false;

    await loadOfficeJsApi();
    await new Promise<void>((resolve) =>
      Office.onReady(() => {
        state.loaded = true;
        state.enabled = Office.context?.host === Office.HostType.Word;

        dispatch("office-ready", state.enabled);
        resolve();
      })
    );
  } catch {
    console.error("Failed to load Office JS API");
  }
}

export function initOnDOMContentLoaded(): Promise<void> {
  return new Promise((resolve) => {
    document.addEventListener("DOMContentLoaded", async () => {
      await init();
      resolve();
    });
  });
}

/**
 * Registers a callback to be invoked when Office.js is ready.
 * 
 * Handles the race condition where modules may be imported after the
 * "office-ready" event has already fired. If Office is already loaded,
 * the callback is invoked immediately; otherwise, it listens for the event.
 * 
 * @param callback - Function called with `enabled` (true if Word host)
 */
export function onOfficeReady(callback: (enabled: boolean) => void): void {
  if (state.loaded) {
    callback(state.enabled ?? false);
  } else {
    document.addEventListener("office-ready", (e: CustomEvent<boolean>) => {
      callback(e.detail);
    }, { once: true });
  }
}

export default state;
